import { Box, Paper, Typography } from "@mui/material";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import type { ReactElement, ReactNode } from "react";

export const PANEL_BORDER = "#dde1ea";
export const ROW_LINE = "#e8ebf1";
export const HEADER_ROW_BG = "#f6f7fa";

/** The four text roles a page uses; nothing else sets a font size. */
export const TEXT = {
	title: { fontSize: 15, fontWeight: 600 },
	body: { fontSize: 13, fontWeight: 400 },
	label: { fontSize: 13, fontWeight: 600 },
	dense: { fontSize: 12, fontWeight: 400 },
	head: { fontSize: 12, fontWeight: 600 },
	secondary: { fontSize: 11, fontWeight: 400 },
} as const;

export const EDIT_ICON_SX = { fontSize: 14, color: "text.secondary" } as const;

/** A table row carries a bottom rule; the last one does not. */
export const tableRowsSx = {
	"& td, & th": { borderBottom: `1px solid ${ROW_LINE}` },
	"& tbody tr:last-of-type td": { borderBottom: 0 },
	"& thead th": { bgcolor: HEADER_ROW_BG },
} as const;

/** Grid inside a panel: no own frame, same header band and rules as a table. */
export const panelGridSx = {
	"& .ag-root-wrapper": { border: 0, borderRadius: 0 },
	"& .ag-header": {
		bgcolor: HEADER_ROW_BG,
		borderBottom: `1px solid ${ROW_LINE}`,
	},
	"& .ag-header-cell-text": { ...TEXT.head, color: "text.secondary" },
	"& .ag-row": { borderBottom: `1px solid ${ROW_LINE}` },
	"& .ag-row-last": { borderBottom: 0 },
	"& .ag-cell.stand-property-cell": { paddingLeft: "12px !important" },
} as const;

interface SectionPanelProps {
	title: string;
	icon?: ReactElement;
	/** Right side of the header bar. */
	actions?: ReactNode;
	/** Body runs edge to edge, for a grid or a table with its own rules. */
	flush?: boolean;
	sx?: SxProps<Theme>;
	children: ReactNode;
}

export const SectionPanel = ({
	title,
	icon,
	actions,
	flush = false,
	sx,
	children,
}: SectionPanelProps) => (
	<Paper
		elevation={3}
		sx={[
			{
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
				minWidth: 0,
			},
			...(Array.isArray(sx) ? sx : sx ? [sx] : []),
		]}
	>
		<Box
			sx={(t) => ({
				display: "flex",
				alignItems: "center",
				gap: 1,
				minHeight: 28,
				px: 1.5,
				bgcolor: alpha(t.palette.primary.main, 0.07),
				borderBottom: `1px solid ${PANEL_BORDER}`,
				flexShrink: 0,
			})}
		>
			{icon && (
				<Box
					sx={{
						display: "flex",
						color: "primary.main",
						"& svg": { fontSize: 18 },
					}}
				>
					{icon}
				</Box>
			)}
			<Typography sx={{ ...TEXT.title, color: "text.primary" }} noWrap>
				{title}
			</Typography>
			<Box sx={{ flex: 1 }} />
			{actions}
		</Box>
		<Box sx={{ flex: 1, minHeight: 0, ...(flush ? {} : { p: 1 }) }}>
			{children}
		</Box>
	</Paper>
);
